import { type ReactNode, useMemo, useState } from 'react';
import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { useQueries, useQuery } from '@tanstack/react-query';
import { useSession } from './App';
import { organizationAccessTarget, projectStatusLabels } from './hr-permissions';
import { countryOptions, timezoneLabel } from './location-options';
import { permissionLabel, permissionGroupLabel, ruValues, auditEventLabel } from './i18n';
import { ReviewSubmissionLinks } from './review-pages';
import { StructuredData } from './structured-data';
import { field, getObject, name, type GetPath, type Row } from './api/client';
import { allRows, useAccess, useRows, useCompleteRows } from './api/data';
import { human, label, relation, canPerformAction, type Action } from './forms';
import { findResource, resources, sectionTitles, type Resource, type Section } from './modules';
import { isSharedWorkAreaPath, permittedWorkAreas, workAreaLabels } from './work-areas';
import {
  Select,
  Avatar,
  Button,
  Empty,
  ErrorState,
  Icon,
  Loading,
  Overlay,
  Search,
  Status,
  Table,
  Tabs,
  type Column,
} from './ui';

export function useRelations(keys: string[]) {
  const access = useAccess();
  const hasHr = permittedWorkAreas(access.data?.workAreas).includes('HR');
  const paths = useMemo(
    () =>
      [...new Set(keys.flatMap((key) => (relation(key) ? [relation(key)!] : [])))].filter(
        (path) => hasHr || ['/api/v1/accounts', '/api/v1/permission-groups'].includes(path),
      ),
    [keys.join(','), hasHr],
  );
  const queries = useQueries({
    queries: paths.map((path) => ({
      queryKey: ['choices', path],
      queryFn: ({ signal }: { signal: AbortSignal }) => allRows(path, {}, signal),
      staleTime: 30_000,
    })),
  });
  return Object.fromEntries(
    paths.map((path, index) => [path, queries[index]?.data || []]),
  ) as Record<string, Row[]>;
}
function resourceFieldLabel(resource: Resource, key: string) {
  return resource.key === 'teams' && key === 'startDate' ? 'Дата начала' : label(key);
}
function recordTitle(resource: Resource, row: Row, related: Record<string, Row[]>): string {
  if (resource.key === 'permission-groups') return permissionGroupLabel(field(row, 'name'));
  if (resource.key === 'audit') return auditEventLabel(field(row, 'eventType'));
  for (const key of [
    'name',
    'workName',
    'title',
    'internalTitle',
    'login',
    'employeeId',
    'candidateId',
    'applicationId',
    'jurisdiction',
  ]) {
    const value = field(row, key);
    if (!value) continue;
    const path = relation(key);
    if (!path) return value;
    const record = related[path]?.find((item) => item.id === value);
    if (record) return name(record) || field(record, 'login') || resource.singular;
  }
  return resource.singular;
}
function Display({
  row,
  keyName,
  related,
  resourceKey,
}: {
  row: Row;
  keyName: string;
  resourceKey?: string;
  related: Record<string, Row[]>;
}) {
  const value = row[keyName];
  if (value === null || value === undefined || value === '')
    return <span className="muted">—</span>;
  if (keyName === 'name' && resourceKey === 'permission-groups')
    return <>{permissionGroupLabel(String(value))}</>;
  if (keyName === 'countryCode') {
    const country = countryOptions.find((item) => item.id === value);
    return <>{country ? `${country.name} / ${country.id}` : String(value)}</>;
  }
  if (keyName === 'timezone') return <>{timezoneLabel(String(value))}</>;
  if (keyName.toLowerCase().includes('status'))
    return (
      <Status
        value={String(value)}
        label={resourceKey === 'teams' ? projectStatusLabels[String(value)] : undefined}
      />
    );
  if (keyName === 'eventType') return <>{auditEventLabel(String(value))}</>;
  if (typeof value === 'string' && ruValues[value]) return <>{ruValues[value]}</>;
  if (keyName === 'coverage') return <>{String(value)}%</>;
  const path = relation(keyName);
  if (path) {
    const ids = Array.isArray(value) ? value : [value];
    return (
      <>
        {ids.map((id, index) => {
          const item = related[path]?.find((record) => record.id === id);
          const text = name(item) || field(item, 'login');
          return (
            <span key={String(id)}>
              {index > 0 ? ', ' : ''}
              {text || 'Нет доступа к записи'}
            </span>
          );
        })}
      </>
    );
  }
  if (
    keyName.endsWith('At') ||
    keyName.endsWith('Date') ||
    ['periodStart', 'periodEnd', 'reviewDueAt'].includes(keyName)
  )
    return (
      <>
        {new Date(String(value)).toLocaleDateString('ru-RU', {
          day: 'numeric',
          month: 'short',
          year: 'numeric',
        })}
      </>
    );
  if (keyName === 'progressPercent')
    return (
      <div className="progress-label">
        <span>{String(value)}%</span>
        <progress value={Number(value)} max={100} />
      </div>
    );
  if (typeof value === 'boolean') return <>{value ? 'Да' : 'Нет'}</>;
  if (keyName === 'definition' && value && typeof value === 'object')
    return <>{String((value as Record<string, unknown>).title || 'Задача')}</>;
  if (Array.isArray(value))
    return (
      <>
        {value
          .filter((item) => typeof item === 'string' && !/^[0-9a-f-]{36}$/i.test(item))
          .join(', ') || `${value.length} выбрано`}
      </>
    );
  if (typeof value === 'object' || /^[0-9a-f-]{36}$/i.test(String(value)))
    return <span className="muted">Связанная запись</span>;
  return <>{String(value)}</>;
}
export function ChildRecords({
  title,
  path,
  params,
  query = {},
  columns,
  onAction,
  rowActions,
  collection,
  referencePath,
}: {
  title: string;
  path: GetPath;
  params?: Record<string, string>;
  query?: Record<string, string>;
  columns: string[];
  onAction?: (row: Row) => void;
  rowActions?: (row: Row) => ReactNode;
  collection?: string;
  referencePath?: GetPath;
}) {
  const list = useRows(path, query, { params, enabled: !collection });
  const object = useQuery({
    queryKey: ['child-collection', path, params],
    enabled: Boolean(collection),
    queryFn: ({ signal }) => getObject<Record<string, unknown>>(path, params || {}, signal),
  });
  const reference = useQuery({
    queryKey: ['child-references', referencePath],
    enabled: Boolean(referencePath),
    queryFn: ({ signal }) => allRows(referencePath!, {}, signal),
  });
  const values = collection ? (object.data?.[collection] as (Row | string)[]) || [] : [];
  const rows = collection
    ? referencePath
      ? (reference.data || []).filter((row) => values.includes(row.id))
      : (values as Row[])
    : list.rows;
  const error = list.error || object.error || reference.error;
  const related = useRelations(columns);
  return (
    <section className="detail-section">
      <h3>{title}</h3>
      {error ? (
        <ErrorState error={error} />
      ) : (
        <Table
          rows={rows}
          loading={list.isLoading || object.isLoading || reference.isLoading}
          columns={[
            ...columns.map((key) => ({
              key,
              label: label(key),
              render: (row: Row) => <Display row={row} keyName={key} related={related} />,
            })),
            ...(rowActions
              ? [{ key: 'actions', label: '', sortable: false, filter: false, render: rowActions }]
              : []),
          ]}
          empty={`Пока нет записей: ${title.toLowerCase()}`}
          onRow={onAction}
        />
      )}
      {list.hasNextPage && (
        <Button onClick={() => void list.fetchNextPage()} disabled={list.isFetchingNextPage}>
          Загрузить ещё
        </Button>
      )}
    </section>
  );
}
export function EntityDetail({
  resource,
  record,
  related,
  onAction,
  onRelated,
  tab = 'overview',
  onTab,
  onChildAction,
}: {
  resource: Resource;
  record: Row;
  related: Record<string, Row[]>;
  onClose: () => void;
  tab?: string;
  onTab: (tab: string) => void;
  onAction: (action: Action) => void;
  onChildAction: (action: Action, childKey: string, childId: string) => void;
  onRelated: (resource: Resource, row: Row) => void;
}) {
  const access = useAccess();
  const detail = useQuery({
    queryKey: ['detail', resource.key, record.id],
    enabled: Boolean(resource.detail),
    queryFn: ({ signal }) =>
      getObject<Row>(resource.detail!, { [resource.idParam || 'id']: record.id }, signal),
  });
  const row = detail.data || record;
  const target = organizationAccessTarget(resource.key, row);
  const feedback = useQuery({
    queryKey: ['interview-feedback', row.id],
    enabled: resource.key === 'interviews',
    queryFn: ({ signal }) =>
      getObject<Row>('/api/v1/interviews/{id}/feedback-form', { id: row.id }, signal),
  });
  const analytics = useQuery({
    queryKey: ['job-analytics', row.id],
    enabled: resource.key === 'job-postings' && tab === 'analytics',
    queryFn: ({ signal }) =>
      getObject<{
        totalCandidates: number;
        activeByStage: Record<string, number>;
        stageNames?: Record<string, string>;
        rejected: number;
        offers: number;
        hired: number;
      }>('/api/v1/job-postings/{id}/analytics', { id: row.id }, signal),
  });
  const children = (resource.children?.(row) || []).filter(
    (child) => !child.permission || access.can(child.permission),
  );
  const actions = (resource.actions?.(row) || []).filter(
    (action) =>
      (!action.path.endsWith('/work-areas') ||
        ['accounts.manage', 'permissions.manage'].every((permission) =>
          access.can(permission, {
            entityType: 'Workspace',
            entityId: access.data?.account.workspaceId,
          }),
        )) &&
      (action.path !== '/api/v1/interviews/{id}/feedback' || feedback.data?.canSubmit === true) &&
      (canPerformAction(action, (permission) => access.can(permission, target)) ||
        (action.path === '/api/v1/interviews/{id}/feedback' &&
          access.can('interviews.feedback.all'))),
  );
  const fields = [
    ...new Set([
      ...resource.fields,
      ...(['candidates'].includes(resource.key)
        ? ['phone', 'tags', 'currentEmployer']
        : ['job-postings'].includes(resource.key)
          ? ['description', 'audience', 'confidential', 'hiringManagerIds']
          : ['onboarding', 'offboarding'].includes(resource.key)
            ? ['reason', 'terminationType', 'contractEndDate', 'buddyId']
            : []),
    ]),
  ];
  const taskPath =
    resource.key === 'onboarding'
      ? '/api/v1/onboarding-processes/{id}/tasks'
      : resource.key === 'offboarding'
        ? '/api/v1/offboarding-processes/{id}/tasks'
        : null;
  return (
    <section className="entity-page">
      <div className="entity-detail">
        <div className="entity-summary">
          <div className="entity-heading">
            <Avatar label={recordTitle(resource, row, related)} size={56} />
            <div>
              <p className="muted">{resource.singular}</p>
              <h2>{recordTitle(resource, row, related)}</h2>
            </div>
          </div>
          {detail.error && <ErrorState error={detail.error} />}
          <div className="entity-actions">
            {resource.edit &&
              (resource.canEdit
                ? resource.canEdit(row)
                : !resource.hidden || field(row, 'status') === 'DRAFT') &&
              access.can(resource.editPermission || resource.managePermission || '', target) && (
                <Button
                  icon="Pencil"
                  onClick={() =>
                    onAction({
                      label: `Изменить: ${resource.singular.toLowerCase()}`,
                      method: 'PATCH',
                      path: resource.edit!,
                      params: { [resource.idParam || 'id']: row.id },
                      initial: row,
                    })
                  }
                >
                  Изменить
                </Button>
              )}
            {actions.map((action) => (
              <Button key={action.label} onClick={() => onAction(action)}>
                {action.label}
              </Button>
            ))}
          </div>
        </div>
        <Tabs
          value={tab}
          onChange={onTab}
          tabs={[
            { label: 'Обзор', value: 'overview', icon: 'NationalId' },
            ...(resource.key === 'job-postings'
              ? [{ label: 'Аналитика', value: 'analytics', icon: 'StarFilled' }]
              : []),
            ...children.map((child) => ({
              label: child.title,
              value: child.key,
              icon: 'DocumentsPair',
            })),
            ...(taskPath ? [{ label: 'Задачи', value: 'tasks', icon: 'DocumentsPair' }] : []),
            ...(resource.key === 'applications'
              ? [
                  { label: 'Воронка', value: 'pipeline', icon: 'People' },
                  { label: 'Интервью', value: 'interviews', icon: 'Calendar' },
                  { label: 'Офферы', value: 'offers', icon: 'DocumentsPair' },
                ]
              : []),
            ...(resource.key === 'candidates'
              ? [
                  { label: 'Отклики', value: 'applications', icon: 'People' },
                  { label: 'Цели обработки', value: 'processing', icon: 'DocumentsPair' },
                ]
              : []),
            ...(resource.key === 'permission-groups'
              ? [{ label: 'Разрешения', value: 'grants', icon: 'Controls' }]
              : []),
          ]}
        />
        {resource.key === 'reviews' && tab === 'overview' && <ReviewSubmissionLinks review={row} />}
        {resource.key === 'job-postings' &&
          tab === 'analytics' &&
          (analytics.isLoading ? (
            <Loading />
          ) : analytics.error ? (
            <ErrorState error={analytics.error} />
          ) : (
            analytics.data && (
              <div className="job-analytics">
                <div className="overview-stats">
                  {[
                    ['Кандидаты', analytics.data.totalCandidates],
                    ['Отклонены', analytics.data.rejected],
                    ['Офферы', analytics.data.offers],
                    ['Наняты', analytics.data.hired],
                  ].map(([title, count]) => (
                    <div className="panel overview-stat" key={title}>
                      <strong>{count}</strong>
                      <span>{title}</span>
                    </div>
                  ))}
                </div>
                <section className="panel analytics-stages">
                  <h2>Активные кандидаты по этапам</h2>
                  <Table
                    rows={Object.entries(analytics.data.activeByStage).map(([id, count]) => ({
                      id,
                      name: analytics.data?.stageNames?.[id] || 'Этап найма',
                      count,
                    }))}
                    columns={[
                      { key: 'name', label: 'Этап', width: 350 },
                      { key: 'count', label: 'Кандидаты', width: 180 },
                    ]}
                    empty="Активных кандидатов пока нет"
                  />
                </section>
              </div>
            )
          ))}
        {children
          .filter((child) => child.key === tab)
          .map((child) => (
            <ChildRecords
              key={child.key}
              title={child.title}
              path={child.path}
              params={child.params}
              query={child.query}
              collection={child.collection}
              referencePath={child.referencePath}
              columns={child.fields}
              onAction={
                child.targetResource
                  ? (record) =>
                      onRelated(findResource(resource.section, child.targetResource!)!, record)
                  : undefined
              }
              rowActions={
                child.actions
                  ? (record) => (
                      <div className="row-actions">
                        {child.actions!(record, row)
                          .filter((action) => canPerformAction(action, access.can))
                          .map((action) => (
                            <Button
                              key={action.label}
                              onClick={() => onChildAction(action, child.key, record.id)}
                            >
                              {action.label}
                            </Button>
                          ))}
                      </div>
                    )
                  : undefined
              }
            />
          ))}
        {tab === 'overview' && (
          <div className="details-list">
            {fields
              .filter((key) => !row[key] || typeof row[key] !== 'object')
              .map((key) => (
                <div key={key}>
                  <span>{resourceFieldLabel(resource, key)}</span>
                  <span>
                    <Display row={row} keyName={key} related={related} resourceKey={resource.key} />
                  </span>
                </div>
              ))}
          </div>
        )}
        {tab === 'overview' && resource.key === 'accounts' && row.type !== 'SERVICE_ACCOUNT' && (
          <div className="details-list work-area-access-summary">
            <div>
              <span>Доступные пространства</span>
              <span>
                {permittedWorkAreas(row.workAreas)
                  .map((area) => workAreaLabels[area])
                  .join(', ') || 'Не назначены'}
              </span>
            </div>
          </div>
        )}
        {tab === 'overview' &&
          fields
            .filter((key) => row[key] && typeof row[key] === 'object')
            .map((key) => (
              <div key={key}>
                <h3 className="content-heading">{label(key)}</h3>
                <StructuredData value={row[key]} />
              </div>
            ))}
        {taskPath && tab === 'tasks' && (
          <ChildRecords
            title="Задачи"
            path={taskPath}
            params={{ id: row.id }}
            columns={['title', 'assigneeEmployeeId', 'dueAt', 'status']}
            onAction={(task) => {
              if (task.canComplete === true && access.can(resource.managePermission || ''))
                onAction({
                  label: 'Завершить задачу',
                  path: '/api/v1/process-tasks/{id}/complete',
                  params: { id: task.id },
                  initial: { version: task.version },
                });
            }}
          />
        )}
        {resource.key === 'applications' && (
          <>
            {tab === 'pipeline' && (
              <ChildRecords
                title="Воронка найма"
                path="/api/v1/applications/{id}/pipeline"
                params={{ id: row.id }}
                columns={['name', 'type', 'order']}
              />
            )}
            {tab === 'interviews' && (
              <ChildRecords
                title="Интервью"
                path="/api/v1/interviews"
                query={{ applicationId: row.id }}
                columns={['startsAt', 'durationMinutes', 'status']}
                onAction={(child) => onRelated(findResource('recruitment', 'interviews')!, child)}
              />
            )}
            {tab === 'offers' && (
              <ChildRecords
                title="Офферы"
                path="/api/v1/offers"
                query={{ applicationId: row.id }}
                columns={['approvalStatus', 'lifecycleStatus', 'expiresAt']}
                onAction={(child) => onRelated(findResource('recruitment', 'offers')!, child)}
              />
            )}
          </>
        )}
        {resource.key === 'candidates' && tab === 'applications' && (
          <ChildRecords
            title="Отклики"
            path="/api/v1/applications"
            query={{ candidateId: row.id }}
            columns={['jobPostingId', 'status', 'createdAt']}
            onAction={(child) => onRelated(findResource('recruitment', 'applications')!, child)}
          />
        )}
        {resource.key === 'candidates' && tab === 'processing' && (
          <ChildRecords
            title="Цели обработки"
            path="/api/v1/candidates/{id}/processing-basis-records"
            params={{ id: row.id }}
            columns={['purpose', 'jurisdiction', 'basis', 'effectiveAt']}
          />
        )}
        {resource.key === 'accounts' && tab === 'sessions' && (
          <ChildRecords
            title="Сессии"
            path="/api/v1/accounts/{accountId}/sessions"
            params={{ accountId: row.id }}
            columns={['createdAt', 'expiresAt', 'status']}
          />
        )}
        {['role-profiles', 'skills', 'employee-evaluation-templates'].includes(resource.key) &&
          tab === 'versions' && (
            <ChildRecords
              title="Версии"
              rowActions={(version) =>
                field(version, 'status') === 'DRAFT' &&
                access.can(resource.managePermission || '') ? (
                  <Button
                    onClick={() =>
                      onAction({
                        label: 'Опубликовать версию',
                        path: `/api/v1/${resource.key === 'role-profiles' ? 'role-profile-versions' : resource.key === 'skills' ? 'skill-versions' : 'employee-evaluation-template-versions'}/{id}/publish` as Action['path'],
                        params: { id: version.id },
                        initial: { version: version.version },
                        permission: resource.managePermission,
                      })
                    }
                  >
                    Опубликовать
                  </Button>
                ) : null
              }
              path={`/api/v1/${resource.key}/{id}/versions` as GetPath}
              params={{ id: row.id }}
              columns={['versionNumber', 'status', 'publishedAt']}
              onAction={(version) => {
                const prefix =
                  resource.key === 'role-profiles'
                    ? 'role-profile-versions'
                    : resource.key === 'skills'
                      ? 'skill-versions'
                      : 'employee-evaluation-template-versions';
                if (field(version, 'status') === 'DRAFT')
                  onAction({
                    label: 'Редактировать версию',
                    method: 'PATCH',
                    path: `/api/v1/${prefix}/{id}` as Action['path'],
                    params: { id: version.id },
                    initial: version,
                  });
              }}
            />
          )}
        {resource.key === 'permission-groups' && tab === 'grants' && Array.isArray(row.grants) && (
          <section className="detail-section">
            <h3>Разрешения и области доступа</h3>
            {(row.grants as Record<string, unknown>[]).map((grant, index) => (
              <div className="permission-row" key={index}>
                <span>{permissionLabel(String(grant.permission))}</span>
                <span className="muted">{human(String(grant.scope))}</span>
              </div>
            ))}
          </section>
        )}
      </div>
    </section>
  );
}
export function DomainPage({
  section: fixedSection,
  resource: fixedKey,
  employeeId,
}: { section?: Section; resource?: string; employeeId?: string } = {}) {
  const location = useRouterState({ select: (state) => state.location.pathname });
  const parts = location.split('/');
  const section = fixedSection || (parts[1] as Section);
  const key = fixedKey || parts[2] || '';
  const resource = findResource(section, key);
  const access = useAccess();
  if (access.isLoading) return <Loading />;
  if (access.error) return <ErrorState error={access.error} retry={() => void access.refetch()} />;
  if (!resource) return <Empty title="Страница не найдена" subtitle="Выберите раздел в меню." />;
  if (
    !(resource.readPermissions || [resource.readPermission]).some((permission) =>
      access.can(permission),
    )
  )
    return (
      <Empty
        title="Нет доступа к разделу"
        subtitle="У вашей учётной записи нет доступа к этим данным."
      />
    );
  return <ResourceScreen key={`${section}:${key}`} resource={resource} employeeId={employeeId} />;
}
function ResourceScreen({ resource, employeeId }: { resource: Resource; employeeId?: string }) {
  const session = useSession();
  const [selectedEmployee, setSelectedEmployee] = useState(
    employeeId || session.account?.employeeId || '',
  );
  const employees = useCompleteRows('/api/v1/employees', Boolean(resource.employeeScoped));
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [columnsOpen, setColumnsOpen] = useState(false);
  const [hidden, setHidden] = useState<string[]>([]);
  const access = useAccess();
  const list = useRows(
    resource.path,
    { search: search || undefined },
    {
      params: resource.employeeScoped ? { id: selectedEmployee } : undefined,
      enabled: !resource.employeeScoped || Boolean(selectedEmployee),
    },
  );
  const related = useRelations([
    ...resource.fields,
    'name',
    'workName',
    'employeeId',
    'candidateId',
    'applicationId',
  ]);
  const rows = list.rows.filter(
    (row) =>
      (status === 'all' || field(row, resource.statusKey || 'status') === status) &&
      (!search ||
        resource.fields.some((key) =>
          field(row, key).toLowerCase().includes(search.toLowerCase()),
        )),
  );
  const statuses = [
    ...new Set(list.rows.map((row) => field(row, resource.statusKey || 'status')).filter(Boolean)),
  ];
  const columns: Column<Row>[] = resource.fields
    .filter((key) => !hidden.includes(key))
    .map((key, index) => ({
      key,
      label: resourceFieldLabel(resource, key),
      width: index === 0 ? 300 : key.includes('Электронная почта') ? 260 : 180,
      render: (row) =>
        index === 0 ? (
          <div className="person-cell">
            <Avatar label={recordTitle(resource, row, related)} />
            <span>{recordTitle(resource, row, related)}</span>
          </div>
        ) : (
          <Display row={row} keyName={key} related={related} resourceKey={resource.key} />
        ),
    }));
  const tabs = resources.filter(
    (item) =>
      item.section === resource.section &&
      (permittedWorkAreas(access.data?.workAreas).includes('HR') ||
        isSharedWorkAreaPath(`/${item.section}/${item.key}`)) &&
      !item.hidden &&
      (item.readPermissions || [item.readPermission]).some((permission) => access.can(permission)),
  );
  return (
    <>
      <div className="domain-tabs">
        <Tabs
          tabs={tabs.map((item) => ({ label: item.title, value: item.key }))}
          value={resource.key}
          onChange={(value) => {
            void navigate({
              to: '/$section/$resource',
              params: { section: resource.section, resource: value },
            });
          }}
        />
      </div>
      {resource.employeeScoped && (
        <div className="employee-scope">
          <label>
            Сотрудник
            <Select
              value={selectedEmployee}
              onChange={(event) => setSelectedEmployee(event.target.value)}
            >
              <option value="">Выберите сотрудника</option>
              {employees.rows.map((row) => (
                <option key={row.id} value={row.id}>
                  {name(row)}
                </option>
              ))}
            </Select>
          </label>
          {employees.error && <ErrorState error={employees.error} />}
        </div>
      )}
      <section className="panel data-panel">
        <div className="domain-counts">
          <div>
            <span className="stat-count">
              {list.rows.length}
              {list.hasNextPage ? '+' : ''}
            </span>
            <span>{resource.title}</span>
          </div>
          <p className="muted">{resource.description}</p>
        </div>
        <div className="table-toolbar">
          <Search
            value={search}
            onChange={setSearch}
            placeholder={`Поиск: ${resource.title.toLowerCase()}`}
          />
          {statuses.length > 0 && (
            <Select
              className="pill"
              aria-label="Фильтр по статусу"
              value={status}
              onChange={(event) => setStatus(event.target.value)}
            >
              <option value="all">Все статусы</option>
              {statuses.map((value) => (
                <option key={value} value={value}>
                  {human(value)}
                </option>
              ))}
            </Select>
          )}
          <span className="toolbar-spacer" />
          {resource.key === 'candidates' && access.can('candidates.merge') && (
            <Button onClick={() => void navigate({ to: '/recruitment/candidates/merge' })}>
              Объединить дубликаты
            </Button>
          )}
          {resource.key === 'candidates' && access.can('candidates.import') && (
            <Button
              onClick={() => void navigate({ to: '/operations/$mode', params: { mode: 'import' } })}
            >
              Импорт
            </Button>
          )}
          {resource.create &&
            (resource.key !== 'organizations' ||
              (!list.isLoading &&
                !list.isError &&
                !list.rows.some((row) => row.status === 'ACTIVE'))) &&
            access.can(resource.createPermission || resource.managePermission || '') &&
            (resource.key !== 'accounts' ||
              ['accounts.manage', 'permissions.manage'].every((permission) =>
                access.can(permission, {
                  entityType: 'Workspace',
                  entityId: access.data?.account.workspaceId,
                }),
              )) &&
            (!resource.employeeScoped || Boolean(selectedEmployee)) && (
              <Button
                icon="Plus"
                onClick={() => {
                  void navigate({
                    to: '/$section/$resource/new',
                    params: { section: resource.section, resource: resource.key },
                    search: { employeeId: resource.employeeScoped ? selectedEmployee : undefined },
                  });
                }}
              >
                Создать
              </Button>
            )}
          <Button icon="16/ColumnSettings" onClick={() => setColumnsOpen(true)}>
            Столбцы
          </Button>
        </div>
        {list.error ? (
          <ErrorState error={list.error} retry={() => void list.refetch()} />
        ) : (
          <Table
            rows={rows}
            columns={columns}
            loading={list.isLoading}
            empty={`В разделе «${resource.title}» пока нет записей`}
            onRow={(row) => {
              void navigate({
                to: '/$section/$resource/$entityId',
                params: { section: resource.section, resource: resource.key, entityId: row.id },
              });
            }}
          />
        )}
        {list.hasNextPage && (
          <div className="load-more">
            <Button disabled={list.isFetchingNextPage} onClick={() => void list.fetchNextPage()}>
              Загрузить ещё
            </Button>
          </div>
        )}
      </section>
      {columnsOpen && (
        <Overlay title="Столбцы" variant="dialog" onClose={() => setColumnsOpen(false)}>
          <div className="filter-options">
            {resource.fields.map((key) => (
              <label key={key}>
                <input
                  type="checkbox"
                  checked={!hidden.includes(key)}
                  disabled={!hidden.includes(key) && hidden.length === resource.fields.length - 1}
                  onChange={(event) =>
                    setHidden((previous) =>
                      event.target.checked
                        ? previous.filter((item) => item !== key)
                        : [...previous, key],
                    )
                  }
                />
                {label(key)}
              </label>
            ))}
          </div>
          <footer>
            <Button onClick={() => setColumnsOpen(false)}>Готово</Button>
          </footer>
        </Overlay>
      )}
    </>
  );
}
const dashboardCards = [
  {
    title: 'Сотрудники',
    path: '/api/v1/employees' as GetPath,
    to: '/people/employees',
    icon: 'People',
    permission: 'employees.read',
  },
  {
    title: 'Вакансии',
    path: '/api/v1/job-postings' as GetPath,
    section: 'recruitment',
    resource: 'job-postings',
    icon: 'DocumentsPair',
    permission: 'job_postings.read',
  },
  {
    title: 'Кандидаты',
    path: '/api/v1/candidates' as GetPath,
    section: 'recruitment',
    resource: 'candidates',
    icon: 'NationalId',
    permission: 'candidates.read',
  },
  {
    title: 'Отклики',
    path: '/api/v1/applications' as GetPath,
    section: 'recruitment',
    resource: 'applications',
    icon: 'DocumentChecked',
    permission: 'candidates.read',
  },
  {
    title: 'Онбординг',
    path: '/api/v1/onboarding-processes' as GetPath,
    section: 'hr',
    resource: 'onboarding',
    icon: 'Utilities',
    permission: 'onboarding.read',
  },
  {
    title: 'Офбординг',
    path: '/api/v1/offboarding-processes' as GetPath,
    section: 'hr',
    resource: 'offboarding',
    icon: 'SidePanelLeftOpen',
    permission: 'offboarding.read',
  },
];
export function OverviewPage() {
  const access = useAccess();
  const cards = dashboardCards.filter((card) => access.can(card.permission));
  const queries = useQueries({
    queries: cards.map((card) => ({
      queryKey: ['overview', card.path],
      queryFn: ({ signal }: { signal: AbortSignal }) => allRows(card.path, {}, signal),
    })),
  });
  const navigate = useNavigate();
  const notifications = useRows('/api/v1/notifications', { unreadOnly: true });
  if (access.isLoading) return <Loading />;
  if (access.error) return <ErrorState error={access.error} retry={() => void access.refetch()} />;
  return (
    <>
      <div className="hr-overview">
        <section className="panel overview-main">
          <header>
            <h2>Рабочее пространство HR</h2>
          </header>
          <div className="overview-stats">
            {cards.map((card, index) => {
              const contents = (
                <>
                  <Icon name={card.icon} />
                  <span className="overview-number">
                    {queries[index]?.isLoading
                      ? '…'
                      : queries[index]?.error
                        ? '—'
                        : queries[index]?.data?.length || 0}
                  </span>
                  <span>{card.title}</span>
                </>
              );
              return card.to ? (
                <Link key={card.path} to="/people/employees" className="overview-stat">
                  {contents}
                </Link>
              ) : (
                <Link
                  key={card.path}
                  to="/$section/$resource"
                  params={{ section: card.section!, resource: card.resource! }}
                  className="overview-stat"
                >
                  {contents}
                </Link>
              );
            })}
          </div>
          {queries.some((query) => query.error) && (
            <ErrorState error={queries.find((query) => query.error)?.error || null} />
          )}
          <div className="overview-quick-actions">
            {access.can('requisitions.manage') && (
              <Button
                icon="Plus"
                onClick={() =>
                  void navigate({
                    to: '/$section/$resource/new',
                    params: { section: 'recruitment', resource: 'requisitions' },
                    search: { employeeId: undefined },
                  })
                }
              >
                Заявка на найм
              </Button>
            )}
            {access.can('employees.create') && (
              <Button
                icon="Plus"
                onClick={() =>
                  void navigate({
                    to: '/$section/$resource/new',
                    params: { section: 'people', resource: 'employees' },
                    search: { employeeId: undefined },
                  })
                }
              >
                Сотрудник
              </Button>
            )}
            {access.can('onboarding.manage') && (
              <Button
                icon="Plus"
                onClick={() =>
                  void navigate({
                    to: '/$section/$resource/new',
                    params: { section: 'hr', resource: 'onboarding' },
                    search: { employeeId: undefined },
                  })
                }
              >
                Онбординг
              </Button>
            )}
          </div>
        </section>
        <section className="panel">
          <header>
            <h2>Задачи</h2>
          </header>
          {notifications.isLoading ? (
            <Loading />
          ) : notifications.error ? (
            <ErrorState error={notifications.error} />
          ) : notifications.rows.filter((row) =>
              ['APPROVALS', 'INTERVIEWS', 'OFFERS', 'PERFORMANCE'].includes(field(row, 'category')),
            ).length ? (
            <div className="todo-list">
              {notifications.rows
                .filter((row) =>
                  ['APPROVALS', 'INTERVIEWS', 'OFFERS', 'PERFORMANCE'].includes(
                    field(row, 'category'),
                  ),
                )
                .slice(0, 6)
                .map((row) => (
                  <div key={row.id}>
                    <Icon name="DocumentChecked" />
                    <span>
                      {field(row, 'title')}
                      <small className="muted">{field(row, 'body')}</small>
                    </span>
                  </div>
                ))}
            </div>
          ) : (
            <Empty
              icon="DocumentChecked"
              title="Всё готово!"
              subtitle="Нет уведомлений, требующих действия"
            />
          )}
        </section>
        <section className="panel overview-processes">
          <header>
            <h2>Жизненный цикл сотрудника</h2>
          </header>
          <div className="lifecycle-links">
            {resources
              .filter(
                (resource) => resource.section === 'hr' && access.can(resource.readPermission),
              )
              .map((resource) => (
                <Link
                  key={resource.key}
                  to="/$section/$resource"
                  params={{ section: 'hr', resource: resource.key }}
                >
                  <Icon name={resource.key === 'templates' ? 'DocumentsPair' : 'Utilities'} />
                  <span>
                    {resource.title}
                    <small className="muted">{resource.description}</small>
                  </span>
                  <Icon name="ChevronRight" />
                </Link>
              ))}
          </div>
        </section>
        <section className="panel">
          <header>
            <h2>Настройка пространства</h2>
          </header>
          <div className="lifecycle-links">
            {(['organisation', 'performance', 'admin'] as Section[])
              .filter((section) =>
                resources.some(
                  (resource) => resource.section === section && access.can(resource.readPermission),
                ),
              )
              .map((section) => {
                const first = resources.find(
                  (resource) => resource.section === section && access.can(resource.readPermission),
                )!;
                return (
                  <Link
                    key={section}
                    to="/$section/$resource"
                    params={{ section, resource: first.key }}
                  >
                    <Icon name="Bank" />
                    <span>{sectionTitles[section]}</span>
                    <Icon name="ChevronRight" />
                  </Link>
                );
              })}
          </div>
        </section>
      </div>
    </>
  );
}
